import { motion } from 'framer-motion';
import { useLanguage } from '@/context/LanguageContext';
import { TID } from '@/lib/testIds';
import { IMAGES } from '@/lib/images';
import { scrollToId } from '@/components/site/Navbar';
import { ArrowRight, Atom, Flask, GraduationCap, GlobeHemisphereEast, Buildings, ShieldStar } from '@phosphor-icons/react';

const PILLAR_ICONS = [Atom, Flask, Buildings, GlobeHemisphereEast, ShieldStar, GraduationCap];

export default function Research() {
  const { t } = useLanguage();
  return (
    <section
      id="research"
      data-testid={TID.research.root}
      className="relative bg-[#F1F5F9] text-[#0F172A] overflow-hidden"
    >
      <div className="absolute inset-0 bg-grid-lines-light pointer-events-none" />

      <div className="relative max-w-7xl mx-auto px-6 md:px-10 py-24 md:py-32">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-start">
          {/* Content */}
          <div className="lg:col-span-7">
            <motion.div
              initial={{ opacity: 0, y: 12 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: '-100px' }}
              transition={{ duration: 0.6 }}
              className="flex items-center gap-3"
            >
              <span className="w-8 h-px bg-[#4F6355]" />
              <span className="text-[11px] tracking-[0.3em] uppercase font-bold text-[#4F6355]">
                {t.research.overline}
              </span>
            </motion.div>

            <motion.h2
              initial={{ opacity: 0, y: 18 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: '-100px' }}
              transition={{ duration: 0.7, delay: 0.05 }}
              className="font-display font-bold tracking-tighter mt-6 text-3xl sm:text-4xl lg:text-5xl text-[#0F172A] leading-[1.05]"
            >
              {t.research.title}
            </motion.h2>

            <motion.p
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true, margin: '-100px' }}
              transition={{ duration: 0.7, delay: 0.12 }}
              className="mt-6 text-base md:text-lg text-[#475569] leading-relaxed max-w-2xl"
            >
              {t.research.body}
            </motion.p>

            <motion.p
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true, margin: '-100px' }}
              transition={{ duration: 0.7, delay: 0.2 }}
              className="mt-4 text-sm md:text-base text-[#475569] leading-relaxed max-w-2xl opacity-90"
            >
              {t.research.body2}
            </motion.p>

            <div className="mt-10 grid grid-cols-2 sm:grid-cols-3 gap-px bg-black/10 border border-black/10">
              {t.research.pillars.map((p, idx) => {
                const Icon = PILLAR_ICONS[idx] || Atom;
                return (
                  <motion.div
                    key={p}
                    initial={{ opacity: 0 }}
                    whileInView={{ opacity: 1 }}
                    viewport={{ once: true, margin: '-80px' }}
                    transition={{ duration: 0.5, delay: idx * 0.05 }}
                    className="bg-[#F1F5F9] p-5 md:p-6 flex flex-col gap-3 hover:bg-white transition-colors"
                  >
                    <Icon size={22} weight="duotone" className="text-[#4F6355]" />
                    <div className="text-sm md:text-base font-semibold tracking-tight text-[#0F172A]">
                      {p}
                    </div>
                  </motion.div>
                );
              })}
            </div>

            <div className="mt-10">
              <button
                data-testid={TID.research.cta}
                onClick={() => scrollToId('contact')}
                className="btn-outline-light inline-flex items-center gap-3 px-6 py-3.5 text-[11px] tracking-[0.22em] uppercase font-bold"
              >
                {t.cta.learnMore}
                <ArrowRight size={14} weight="bold" />
              </button>
            </div>
          </div>

          {/* Image */}
          <div className="lg:col-span-5">
            <div className="sticky top-28 relative aspect-[4/5] overflow-hidden border border-black/10">
              <img
                src={IMAGES.researchCollab}
                alt="Defense research collaboration"
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#0A1128]/40 via-transparent to-transparent" />
              <div className="absolute top-4 left-4 right-4 flex items-center justify-between font-mono text-[10px] tracking-[0.25em] uppercase text-white">
                <span>RES / LAB</span>
                <span>NEXT-GEN</span>
              </div>
              <div className="absolute bottom-0 left-0 right-0 px-5 py-4 bg-gradient-to-t from-[#0A1128] to-transparent">
                <div className="text-white font-display font-bold text-lg tracking-tight">Innovation Ecosystem</div>
                <div className="text-[#94A3B8] text-xs tracking-wide mt-1">Industry · Academia · Government</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
